"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Atom, Shield, HelpCircle } from "lucide-react";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Reveal } from "@/components/cosmic/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const FAQS = [{
  q: "Is this real or scientifically proven?",
  a: "No. Alternate selves and parallel universes are completely unconfirmed by physical experiment. The many-worlds formulation is one of several theoretical interpretations of quantum mechanics, and cosmic multiverse hypotheses remain speculative concepts in theoretical physics. This platform is imaginative, science-inspired fiction and a creative thought experiment—not proven parallel universes, and not real predictions."
}, {
  q: "How are the four alternate lives created?",
  a: "Everything is generated directly in your browser with zero AI services or external LLM calls. A deterministic combinatorial engine pairs your answers with seeded narrative banks and universe models. If you skip any question, plausible picks fill the void. 'Explore Another Possibility' draws a fresh coherent set of variants, while your settings code combined with the variation ID always rebuilds the exact same four lives."
}, {
  q: "Will I get the same results if I come back later?",
  a: "Yes, whenever you open your share link or use a saved set from your library. The settings code and variation ID strictly recreate the identical four lives and cosmic models. Saved sets are stored only in this browser and never sent to a cloud database."
}, {
  q: "What does the Multiverse Life Dossier include?",
  a: "Every visitor can generate and inspect four distinct fictional lives: Divergent, Ascendant, Strange, and Unexpected. The Multiverse Life Dossier includes all complete life chapters: full biography, career & work history, social standing & wealth, home & daily routine, formative turning points & decisions, society & technology, how this world differs from Earth, the detailed 8-dimension Earth comparison ('Compared With My Earth Life'), 'A Day in This Life' narrative beats, three possible future paths, the full chronological timeline, and a downloadable PDF—all completely free, with no account required."
}, {
  q: "How does privacy work?",
  a: "Everything runs in the browser without accounts, trackers, or server databases. Nothing is sent when you download the PDF, and saved settings codes stay on your device."
}, {
  q: "Can I use a universe I designed in Create Your Universe?",
  a: "Yes. You can design custom physical laws in Create Your Universe, then import that universe code into Your Infinite Selves so all four of your fictional timelines inhabit that specific cosmos."
}];
export default function IdeaAndPrivacy() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="idea-and-privacy" data-nav="dark" className="relative w-full max-w-full overflow-hidden bg-transparent text-foreground py-14 sm:py-16 md:py-24 border-b border-border">
      <div className="relative mx-auto max-w-[76rem] px-4 sm:px-6 lg:px-8 w-full">
        <Reveal>
          <div className="flex flex-col items-center text-center mb-16">
            <SectionHeading eyebrow="FOUNDATIONS & ETHICS" title="The Science, The Story & Your Privacy" description="Honest scientific framing, strict client-side data privacy, and answers to common questions about the multiverse model." align="center" as="h2" />
          </div>
        </Reveal>

        {/* Dual Pillar: Two-column obsidian glass notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* About the Idea */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="ql-glass-strong p-6 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4 sm:mb-5 border-b border-border/50 pb-4">
                <div className="p-2 sm:p-2.5 rounded-lg bg-primary/10 border border-primary/30 text-primary shadow-[0_0_12px_var(--glow-primary)] shrink-0">
                  <Atom className="size-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground break-words">
                    About the Idea
                  </h3>
                  <span className="font-mono text-xs text-primary">Theoretical Modeling</span>
                </div>
              </div>

              <div className="space-y-4 text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                <p>
                  The many-worlds formulation is one of several interpretations of quantum mechanics. It posits that every quantum branching event realizes every possible outcome across non-communicating branches. However, it remains actively debated and unconfirmed by empirical physical observation.
                </p>
                <p>
                  Multiverse theories in modern cosmology—such as eternal inflation or string landscape models—are theoretical hypotheses rather than established fact.
                </p>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-border/50">
              <p className="text-foreground/95 text-xs sm:text-sm font-medium border-l-2 border-primary pl-3 py-1 bg-primary/5 rounded-r-lg">
                This platform borrows cosmological hypotheses purely as a storytelling device. The alternate lives generated here are imaginative fiction crafted by a browser-only seeded engine—never real parallel realities, predictive forecasts, or psychological evaluations. Use 'Explore Another Possibility' whenever you wish to examine fresh branches.
              </p>
            </div>
          </motion.div>

          {/* Your Privacy */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="ql-glass-strong p-6 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4 sm:mb-5 border-b border-border/50 pb-4">
                <div className="p-2 sm:p-2.5 rounded-lg bg-primary/10 border border-primary/30 text-primary shadow-[0_0_12px_var(--glow-laser)] shrink-0">
                  <Shield className="size-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground break-words">
                    Your Privacy
                  </h3>
                  <span className="font-mono text-xs text-primary">Transparent Data Policy</span>
                </div>
              </div>

              <div className="space-y-4 text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                <p>
                  Everything runs in the browser with zero account registration, cookies, or profiling. Your chosen nickname and responses are encoded in shareable links, and saved settings codes stay on your device.
                </p>
                <p>
                  Nothing is sent to a server when you explore your alternate lives or download the PDF. Your generated records, timelines, and comparisons are created locally in your browser session.
                </p>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-border/50">
              <p className="text-foreground/95 text-xs sm:text-sm font-medium border-l-2 border-chart-3 pl-3 py-1 bg-chart-3/5 rounded-r-lg">
                Always use a playful pseudonym or nickname. Do not enter phone numbers, physical addresses, or sensitive credentials.
              </p>
            </div>
          </motion.div>
        </div>

        {/* FAQ Accordion Section */}
        <Reveal delay={150}>
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-8">
              <HelpCircle className="size-5 text-primary" />
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground text-center">
                Frequently Asked Questions
              </h3>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-3">
              {FAQS.map((faq, index) => <AccordionItem key={index} value={`faq-${index}`} className="ql-glass border-border px-5 transition-colors data-[state=open]:border-primary/50">
                  <AccordionTrigger className="font-serif text-sm sm:text-base font-semibold text-foreground py-4 hover:no-underline hover:text-primary transition-colors">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4 pt-1">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </Reveal>
      </div>
    </section>;
}